import React from 'react';

/**
 * Lighting thumbnails — rendered-style SVGs that show the EFFECT of
 * each lighting option on a shared subject (a sphere on a floor).
 * All seven variations use the same 120×90 stage and the same sphere;
 * only the subject shading, floor tone, background wall, and shadow
 * differ. The card reads as "what does my product look like with this
 * lighting?" rather than "where is the light rig?".
 *
 * Design rules:
 *   - 120×90 viewBox, 4:3 aspect (matches the other recipe-illustration
 *     sets in this folder).
 *   - Sphere: centered at (60, 42), radius 22. Horizon line y=62.
 *   - Lighting effect is rendered ON the sphere via SVG radial/linear
 *     gradients — no external light-source icons (softbox panels, sun,
 *     etc.). Shadow shape/direction mirrors the light direction.
 *   - Shared warm neutral palette for "bright" variants; colder / tinted
 *     palettes for the moodier variants (dramatic, rim, golden hour).
 *   - Gradient IDs are component-scoped (unique per variant) so multiple
 *     lighting cards can coexist on the canvas without bleed.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const SPHERE_CX = 60;
const SPHERE_CY = 42;
const SPHERE_R = 22;
const HORIZON = 62;

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

// ─── soft diffuse ──────────────────────────────────────────────────
// Broad ambient wrap — gentle top-down gradient, feathered shadow
// fanning out in a big soft ellipse. The bread-and-butter softbox look.
export const SoftDiffuseLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="lt-soft-sphere" cx="42%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="55%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#4A4945" />
            </radialGradient>
            <radialGradient id="lt-soft-shadow" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#1F2022" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#1F2022" stopOpacity="0" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <ellipse cx={SPHERE_CX} cy={HORIZON + 2} rx={SPHERE_R + 8} ry={6} fill="url(#lt-soft-shadow)" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-soft-sphere)" />
    </svg>
);

// ─── flat / even ──────────────────────────────────────────────────
// Minimal gradient, almost no shadow — clinical catalog feel.
export const FlatEvenLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="lt-flat-sphere" cx="50%" cy="38%" r="65%">
                <stop offset="0%" stopColor="#BEBDBA" />
                <stop offset="100%" stopColor="#8A8987" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F0EFEB" />
        <Floor fill="#E4E2DE" />
        <ellipse cx={SPHERE_CX} cy={HORIZON + 1} rx={SPHERE_R + 2} ry={2} fill="#1F2022" opacity="0.07" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-flat-sphere)" />
    </svg>
);

// ─── overhead ──────────────────────────────────────────────────────
// Sharp top-down light — bright crown on the sphere, dark underside,
// tight shadow puddle directly beneath.
export const OverheadLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="lt-over-sphere" cx="50%" cy="8%" r="95%">
                <stop offset="0%" stopColor="#F0EFEB" />
                <stop offset="35%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#2E2D2B" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E0DDD8" />
        <ellipse cx={SPHERE_CX} cy={HORIZON + 1} rx={SPHERE_R - 4} ry={3} fill="#1F2022" opacity="0.45" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-over-sphere)" />
    </svg>
);

// ─── dramatic side ────────────────────────────────────────────────
// Hard 50/50 split — bright left, dark right — with a long hard shadow
// raking across the floor. Moody editorial feel, darker stage.
export const DramaticSideLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <linearGradient id="lt-drama-sphere" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="25%" stopColor="#EFEDE8" />
                <stop offset="55%" stopColor="#6F6E6B" />
                <stop offset="100%" stopColor="#0F0F10" />
            </linearGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#2A2927" />
        <Floor fill="#1A1918" />
        {/* long hard shadow raking right */}
        <path
            d={`M ${SPHERE_CX - 2} ${HORIZON + 1}
                L 118 ${HORIZON + 3}
                L 114 ${HORIZON + 7}
                L ${SPHERE_CX - 6} ${HORIZON + 7} Z`}
            fill="#000000"
            opacity="0.55"
        />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-drama-sphere)" />
    </svg>
);

// ─── rim light ─────────────────────────────────────────────────────
// Dark sphere with a bright halo on the far edge — the whole scene is
// dark, the subject is defined only by its rim.
export const RimLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            {/* warm charcoal BG + soft vignette so the thumbnail reads
                as a moody low-light scene rather than a black square
                at small sizes. */}
            <radialGradient id="lt-rim-bg" cx="50%" cy="50%" r="85%">
                <stop offset="0%" stopColor="#4A433B" />
                <stop offset="100%" stopColor="#201D19" />
            </radialGradient>
            <radialGradient id="lt-rim-sphere" cx="88%" cy="50%" r="72%">
                <stop offset="0%" stopColor="#FFF8E8" />
                <stop offset="16%" stopColor="#D5B48E" />
                <stop offset="38%" stopColor="#3B2E20" />
                <stop offset="100%" stopColor="#221A12" />
            </radialGradient>
            {/* faint glow halo behind the rim-lit side, helps the
                lighting read against the lighter background. */}
            <radialGradient id="lt-rim-halo" cx="65%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#FBE7BC" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#FBE7BC" stopOpacity="0" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="url(#lt-rim-bg)" />
        <Floor fill="#26221D" />
        <ellipse cx={SPHERE_CX + 10} cy={SPHERE_CY} rx={32} ry={26} fill="url(#lt-rim-halo)" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-rim-sphere)" />
    </svg>
);

// ─── natural window ────────────────────────────────────────────────
// Soft directional light from a rectangular window — gentle cool-to-
// warm gradient on the sphere, a subtle window-pane highlight on the
// wall behind so the source reads without drawing the window itself
// as an object.
export const WindowNaturalLighting: React.FC = () => {
    // Small trapezoidal window anchored in the upper-left, reads as
    // a LIGHT SOURCE on a side wall (not the back wall). Three visible
    // light rays + a soft beam project from its face toward the
    // sphere, making the directional relationship unambiguous.
    //
    // Trapezoid corners — strong perspective so the window reads as
    // "angled on a left wall" at thumbnail size:
    //   front-right (closer / taller) → back-left (receding / shorter)
    const fr = { x: 22, y: 10 };   // front-top (right / closer)
    const bk = { x: 6, y: 16 };    // back-top (left / receding)
    const bkB = { x: 6, y: 32 };   // back-bottom (left / receding)
    const frB = { x: 22, y: 38 };  // front-bottom (right / closer)
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const midTop = { x: lerp(fr.x, bk.x, 0.5), y: lerp(fr.y, bk.y, 0.5) };
    const midBot = { x: lerp(frB.x, bkB.x, 0.5), y: lerp(frB.y, bkB.y, 0.5) };
    const hR = { x: fr.x, y: lerp(fr.y, frB.y, 0.5) };
    const hL = { x: bk.x, y: lerp(bk.y, bkB.y, 0.5) };

    const framePoints = `${fr.x},${fr.y} ${bk.x},${bk.y} ${bkB.x},${bkB.y} ${frB.x},${frB.y}`;
    const haloPoints = `${fr.x + 3},${fr.y - 3} ${bk.x - 2},${bk.y - 3} ${bk.x - 2},${bkB.y + 3} ${frB.x + 3},${frB.y + 3}`;
    // Soft directional beam from the window's front edge toward the
    // sphere. Bright at the window side, fading out near the subject.
    const beamPoints = `${fr.x},${fr.y + 3} ${frB.x},${frB.y - 3} 52,50 52,30`;

    // Three visible sun-ray lines radiating from roughly the window's
    // front-edge toward points around the sphere's lit side. Spaced
    // vertically so they look like parallel shafts of light.
    const rayOriginX = fr.x + 1;
    const rayTargetX = SPHERE_CX - SPHERE_R + 4;
    const rays = [
        { y1: fr.y + 4, y2: SPHERE_CY - 10 },
        { y1: lerp(fr.y, frB.y, 0.5), y2: SPHERE_CY },
        { y1: frB.y - 4, y2: SPHERE_CY + 10 },
    ];

    return (
        <svg viewBox="0 0 120 90" style={baseStyle}>
            <defs>
                <linearGradient id="lt-win-sphere" x1="0%" y1="0%" x2="100%" y2="10%">
                    <stop offset="5%" stopColor="#F3EEE4" />
                    <stop offset="55%" stopColor="#9A8771" />
                    <stop offset="100%" stopColor="#3A3025" />
                </linearGradient>
                <radialGradient id="lt-win-shadow" cx="62%" cy="50%" r="55%">
                    <stop offset="0%" stopColor="#1F2022" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="#1F2022" stopOpacity="0" />
                </radialGradient>
                {/* pale sky-to-warmer-bottom gradient inside each pane */}
                <linearGradient id="lt-win-glass" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#FFFDF5" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#E6DECD" stopOpacity="0.8" />
                </linearGradient>
                {/* light beam — bright at window, fades toward subject */}
                <linearGradient id="lt-win-beam" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F0EFEB" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="#F0EFEB" stopOpacity="0" />
                </linearGradient>
            </defs>
            <rect x="0" y="0" width="120" height="90" fill="#F2EEE7" />
            <Floor fill="#E8E2D6" />

            {/* halo glow behind the frame — reads as "this is a light source" */}
            <polygon points={haloPoints} fill="#FBF6EB" opacity="0.45" />

            {/* soft beam projecting from the window face toward the sphere */}
            <polygon points={beamPoints} fill="url(#lt-win-beam)" />

            {/* three visible sun-ray lines — make the light direction
                unambiguous, particularly at small thumbnail sizes */}
            {rays.map((r, i) => (
                <line
                    key={i}
                    x1={rayOriginX}
                    y1={r.y1}
                    x2={rayTargetX}
                    y2={r.y2}
                    stroke="#F5D88F"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                    opacity="0.7"
                />
            ))}

            {/* outer frame (trapezoidal — front edge right, back edge left) */}
            <polygon
                points={framePoints}
                fill="url(#lt-win-glass)"
                stroke="#8A7660"
                strokeWidth="0.7"
            />

            {/* mullions — 2x2 pane grid with perspective-correct slopes */}
            <line x1={midTop.x} y1={midTop.y} x2={midBot.x} y2={midBot.y} stroke="#8A7660" strokeWidth="0.5" />
            <line x1={hR.x} y1={hR.y} x2={hL.x} y2={hL.y} stroke="#8A7660" strokeWidth="0.5" />

            {/* sill — a thin angled ledge under the frame */}
            <polygon
                points={`${bkB.x - 1},${bkB.y + 0.2} ${frB.x + 1},${frB.y + 0.2} ${frB.x + 1},${frB.y + 1.4} ${bkB.x - 1},${bkB.y + 1.4}`}
                fill="#8A7660"
            />

            <ellipse cx={SPHERE_CX + 5} cy={HORIZON + 1} rx={SPHERE_R + 4} ry={3.5} fill="url(#lt-win-shadow)" />
            <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-win-sphere)" />
        </svg>
    );
};

// ─── golden hour ───────────────────────────────────────────────────
// Warm low-angle sunlight — the whole stage takes on an amber cast,
// the sphere transitions from golden highlight on the left to a rich
// rust shadow on the right, and the ground shadow stretches long in
// the same amber family.
export const GoldenHourLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <linearGradient id="lt-gold-bg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FCDFA8" />
                <stop offset="100%" stopColor="#E28F50" />
            </linearGradient>
            <linearGradient id="lt-gold-sphere" x1="0%" y1="0%" x2="100%" y2="20%">
                <stop offset="5%" stopColor="#FDDD9B" />
                <stop offset="55%" stopColor="#B77443" />
                <stop offset="100%" stopColor="#3F2311" />
            </linearGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="url(#lt-gold-bg)" />
        <Floor fill="#B86F35" />
        {/* long amber-tinted shadow */}
        <path
            d={`M ${SPHERE_CX - 2} ${HORIZON + 1}
                L 118 ${HORIZON + 3}
                L 114 ${HORIZON + 8}
                L ${SPHERE_CX - 6} ${HORIZON + 8} Z`}
            fill="#3E2110"
            opacity="0.45"
        />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-gold-sphere)" />
    </svg>
);

// ─── cinematic ─────────────────────────────────────────────────────
// Low-key room: one warm key from the left carves the sphere, the rest
// of the stage falls into deep but readable shadow — a pool of light on
// the floor rather than a lit room.
export const CinematicLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="lt-cine-bg" cx="22%" cy="40%" r="90%">
                <stop offset="0%" stopColor="#5A4C3E" />
                <stop offset="100%" stopColor="#171412" />
            </radialGradient>
            <radialGradient id="lt-cine-pool" cx="40%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#C99A62" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#C99A62" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="lt-cine-sphere" x1="0%" y1="10%" x2="100%" y2="30%">
                <stop offset="8%" stopColor="#F6DDB2" />
                <stop offset="45%" stopColor="#8A6440" />
                <stop offset="100%" stopColor="#15110D" />
            </linearGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="url(#lt-cine-bg)" />
        <Floor fill="#1E1A16" />
        <ellipse cx={SPHERE_CX - 8} cy={HORIZON + 10} rx={44} ry={12} fill="url(#lt-cine-pool)" />
        <path
            d={`M ${SPHERE_CX - 2} ${HORIZON + 1}
                L 118 ${HORIZON + 4}
                L 116 ${HORIZON + 9}
                L ${SPHERE_CX - 6} ${HORIZON + 8} Z`}
            fill="#000000"
            opacity="0.5"
        />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-cine-sphere)" />
    </svg>
);

// ─── evening ───────────────────────────────────────────────────────
// Dusk outside, lamps on inside: a cool blue window behind, a warm glow
// from a lamp on the right lighting the sphere's near side.
export const EveningLighting: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <linearGradient id="lt-eve-window" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#35466B" />
                <stop offset="100%" stopColor="#6C6F8C" />
            </linearGradient>
            <radialGradient id="lt-eve-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD89A" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#FFD89A" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="lt-eve-sphere" cx="80%" cy="30%" r="85%">
                <stop offset="0%" stopColor="#F9D9A6" />
                <stop offset="45%" stopColor="#9A6B3E" />
                <stop offset="100%" stopColor="#1E1712" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#2B2521" />
        <rect x="14" y="10" width="30" height="40" rx="1.5" fill="url(#lt-eve-window)" />
        <Floor fill="#231E1A" />
        <circle cx="100" cy="30" r="22" fill="url(#lt-eve-glow)" />
        <circle cx="100" cy="30" r="3.5" fill="#FFE7BD" />
        <ellipse cx={SPHERE_CX - 6} cy={HORIZON + 2} rx={SPHERE_R + 4} ry={4} fill="#000000" opacity="0.45" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill="url(#lt-eve-sphere)" />
    </svg>
);

export const LIGHTING_ILLUSTRATIONS: Record<string, React.FC> = {
    'cinematic': CinematicLighting,
    'evening': EveningLighting,
    'soft-diffuse': SoftDiffuseLighting,
    'flat-even': FlatEvenLighting,
    'overhead': OverheadLighting,
    'dramatic-side': DramaticSideLighting,
    'rim-light': RimLighting,
    'window-natural': WindowNaturalLighting,
    'golden-hour': GoldenHourLighting,
};
